import { Bolt } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCreateDeviceSignal } from '~/api/device-signals';
import { applyServerErrors } from '~/api/helpers';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { SaveButton } from '~/components/button';
import { PageContainer } from '~/components/page-layout';
import { SettingsBody, SettingsHead } from '~/components/settings';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { DeviceSignalKind, type DeviceSignalTarget } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { DeviceSignalForm, ScheduleFields, serializeSchedule, useSchedule } from './components';
import { signalInputSchema, type Action } from './lib';

export const NewDeviceSignal = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const navigate = useNavigate();

  const link = useLink();

  const [createDeviceSignal] = useCreateDeviceSignal();

  const scheduleFormik = useSchedule(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      action: null as unknown as Action, // invalid
      enabled: true,
      event: {},
      eventTypeId: null,
      kind: DeviceSignalKind.Manual as DeviceSignalKind,
      name: '',
      networkId: currentNetwork.id,
      target: null as unknown as DeviceSignalTarget, // invalid
    },
    onSubmit: async (values, { setErrors }) => {
      const result = await createDeviceSignal({
        variables: {
          payload: {
            ...signalInputSchema.cast(values),
            // Event signals run at their event's time, so they never carry a schedule.
            schedule:
              values.kind === DeviceSignalKind.Event
                ? null
                : serializeSchedule(scheduleFormik.values),
          },
        },
      });
      applyServerErrors(result, setErrors);
      if (result.errors) return;
      navigate(link(`/device-signals`));
    },
    validateOnMount: true,
    validationSchema: signalInputSchema,
  });

  const isEvent = formik.values.kind === DeviceSignalKind.Event;

  return (
    <>
      <Toolbar
        actions={
          <SaveButton
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              !(isEvent || scheduleFormik.isValid) ||
              !formik.dirty
            }
            type="submit"
            form="new-signal"
          />
        }
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<Bolt />}
        titleText={t('pages.device_signals.new_page.title')}
      />

      <PageContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DeviceSignalForm formik={formik} id="new-signal" />
          <Box>
            <SettingsHead>{t('pages.device_signals.schedule_section.header')}</SettingsHead>
            <SettingsBody>
              <ScheduleFields disabled={isEvent} formik={scheduleFormik} />
            </SettingsBody>
          </Box>
        </Box>
      </PageContainer>
    </>
  );
};
